
import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState} from '@microsoft/signalr';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUserContext } from '../../contexts/user-context/UserContext';
import eLynxAuthService from "../../services/authentication/elynx-auth-service/ELynxAuthService";
import cacheService from '../../services/cache-service/CacheService';
import { sleep } from '../../common/utilities/Sleep';


//@@@ need to figure out how to stop connection and remove from cache on logout

export type SignalRProps<T> = {
    customerID: number | undefined,
    userID?: number | undefined,
    methodName: string;
    url: string;
    onCallback: (value: T) => void,
    offline: boolean;
};

export function useSignalR<T>({
    customerID,
    userID,
    url,
    methodName,
    onCallback,
    offline}: SignalRProps<T>): void {

    const [userState] = useUserContext();
    const [hubUrl] = useState(url);
    const [hubConnection, setHubConnection] = useState<HubConnection | undefined>();
    const callbackRef = useRef<(value: T) => void>(undefined);

    //const reconnectRef = useRef(false);

    // const resetConnection = async () => {
    //     try {
    //         await hubConnection?.stop();
    //         hubConnection?.off(methodName);
    //         cacheService.delete(hubUrl);
    //         setHubConnection(undefined);
    //     } catch(error) {
    //         console.log(`restConnection failed ${JSON.stringify(error)}`);
    //     }
    // }


    //console.log(`cacheService size: ${cacheService.size()} keys: ${cacheService.keys()}`);
    //console.log(`cacheService values: ${cacheService.values().map((o => HubConnectionState[(o as HubConnection).state]))}`);


    const resetConnection = useCallback(async () => {
        try {
            console.log(`====> resetConnection: ${hubUrl} ${methodName}`);

            // remove the hanlders first
            hubConnection?.off(methodName);

            //terminate the connection
            await hubConnection?.stop();            

        } catch(error) {
            console.log(`restConnection failed ${JSON.stringify(error)}`);
        } finally {
            // clear the connection from the cache and reset the hub connection. This will force a new connection to be created.
            cacheService.delete(hubUrl);
            setHubConnection(undefined);

            //https://docs.azure.cn/en-us/azure-signalr/signalr-howto-troubleshoot-guide#429-too-many-requests-returned-for-client-requests
            // this article suggests a delay before retrying a start.  after clearing the connection, wait for 2 seconds before continuing.
            await sleep(2000);            
        }
    }, [
        hubConnection, 
        methodName, 
        hubUrl
    ]);

    const getHubConnection = useCallback((): HubConnection | undefined => {

        let connection = cacheService.get(hubUrl) as HubConnection | undefined;

        if (!connection) {

            try {
                console.log(`get connection for ${hubUrl}`);
                connection = new HubConnectionBuilder()
                .withUrl(hubUrl, {
                   accessTokenFactory: () => eLynxAuthService.acquireToken()
                })
                //.withHubProtocol(new MessagePackHubProtocol())

                .withAutomaticReconnect()
                //.configureLogging(LogLevel.Information)
                .configureLogging({
                    log:  (logLevel: LogLevel, message: string) => {

                        //console.log(`useSignalR log => ${message}`)
                        if (logLevel > LogLevel.Information) {
                            console.log(`useSignalR: ${logLevel}, message: ${message}`);
                        }

                        if (logLevel === LogLevel.Error) {
                            try {

                                //https://docs.azure.cn/en-us/azure-signalr/signalr-howto-troubleshoot-guide#429-too-many-requests-returned-for-client-requests
                                // this article suggests a delay before retrying a start.  resetConnection goes a little further by clearing the connection with 
                                // a 2 second delay.
                                resetConnection();

                                //await hubConnection?.stop();
                                // hubConnection?.off(methodName);
                                // cacheService.delete(hubUrl);
                                // setHubConnection(undefined);

                                // reconnectRef.current = true;

                            } catch(error) {
                                console.log(`restConnection failed ${JSON.stringify(error)}`);
                            }
                        }

                }})
                .build();

                cacheService.set(hubUrl, connection);

            }  catch(error) {
                console.log(`Hub connection error: ${hubUrl} - ${JSON.stringify(error)}`);
            }
        }

        return connection;
    }, [
        hubUrl,
        //hubConnection,
        //setHubConnection,
        //methodName,
        resetConnection
    ]);

    const establishConnection = useCallback(() => {
        try {
        const connection = getHubConnection();
        if (connection) {
            setHubConnection(connection);

            //reconnectRef.current = false;
        }
        } catch(error) {
            console.log(`useSignalR establishConnection failed: ${JSON.stringify(error)}`);
        }
    }, [
        getHubConnection
    ]);

    // Remember the latest callback.
    useEffect(() => {

        callbackRef.current = onCallback;

    }, [
        onCallback
    ]);

    useEffect(() => {

        if (hubUrl && !hubConnection && userState.authenticated && !offline) {

            console.log(`====>  useSignalR establishConnection`);

            establishConnection();
        }

    }, [
        hubUrl,
        establishConnection,
        hubConnection,
        setHubConnection,
        userState.authenticated,
        offline
    ]);

    useEffect(() => {

        const start = async () => {
            try {

                console.log(`====> signalR start: ${customerID} ${methodName}`);

                hubConnection?.on(methodName, (value: any) => {
                    if (callbackRef.current) {
                        //callbackRef.current(JSON.parse(value));
                        callbackRef.current(value);
                    }
                });

                if (hubConnection?.state === HubConnectionState.Disconnected) {
                    await hubConnection?.start();
                }

                if (hubConnection?.state === HubConnectionState.Connected) {

                    // unsubscribe first, just in case
                    try {
                        if (userID) {
                            await hubConnection?.invoke('unsubscribe', customerID, userID);
                        } else {
                            await hubConnection?.invoke('unsubscribe', customerID);
                        }

                    } catch(error) {
                        // nothing to do here, go ahead and attempt to subscribe
                    }

                    if (userID) {
                        await hubConnection?.invoke('subscribe', customerID, userID);
                    } else {
                        await hubConnection?.invoke('subscribe', customerID);
                    }

                }

            } catch(error) {

                //setTimeout(start, 1000);
                //cacheService.delete(hubUrl);
                //setTimeout(establishConnection, 1000);
                console.log(`signalR subscribe error: ${JSON.stringify(error)}`);

                //await sleep(2000);

                //https://docs.azure.cn/en-us/azure-signalr/signalr-howto-troubleshoot-guide#429-too-many-requests-returned-for-client-requests
                // this article suggests a delay before retrying a start.  resetConnection goes a little further by clearing the connection with 
                // a 2 second delay.
                resetConnection();
            }
        };

        if (hubConnection && userState.authenticated && customerID && !offline) {
            start();
        }

        return () => {
            if (hubConnection?.state === HubConnectionState.Connected && customerID) {
                try {

                    console.log(`====> signalR unsubscribe: ${customerID} ${methodName}`);


                    hubConnection?.off(methodName);
                    if (userID) {
                        hubConnection?.invoke('unsubscribe', customerID, userID);
                    } else {
                        hubConnection?.invoke('unsubscribe', customerID);
                    }

                } catch(error) {
                    console.log(`signalR subscribe error: ${JSON.stringify(error)}`);
                }
            }
        }

    }, [
        hubConnection,
        methodName,
        userState.authenticated,
        customerID,
        userID,
        offline,
        resetConnection,
        hubUrl
    ]);

    // useEffect(() => {

    //     let timerID = -1;

    //     if (reconnectRef.current) {

    //         timerID = window.setInterval(() => {

    //             console.log(`===> signalR establish connection: ${hubUrl} ${methodName}`);
    //             try {
    //                 establishConnection();
    //             } catch(error) {
    //                 console.log(`signalR reconnect error: ${JSON.stringify(error)}`);
    //             }

    //         }, 5000);
    //     }

    //     return () => {
    //         if (timerID !== -1) {
    //             window.clearInterval(timerID);
    //         }
    //     }
    // });
}
  